/**
 * Utilities for working with ISO calendar date strings (YYYY-MM-DD).
 *
 * These helpers use **local-time** conversion, not UTC. `Date.prototype.toISOString()`
 * converts to UTC, so near midnight in the Europe/Amsterdam timezone the expression
 * `new Date().toISOString().split('T')[0]` can return the wrong calendar day.
 * Always use these helpers instead of inline `toISOString().split('T')[0]`.
 */

/**
 * Convert a Date to an ISO calendar date string (YYYY-MM-DD) using local time.
 */
export function toIsoDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Read an ISO calendar date string (YYYY-MM-DD) as midnight in **local** time.
 *
 * The inverse of {@link toIsoDateString}, and the reason it exists: `new Date('2026-03-15')`
 * is specified to parse a date-only string as UTC, so in Europe/Amsterdam it lands one or
 * two hours *into* the previous day. Comparing appointment start times against that
 * threshold silently drops the ones before 02:00.
 */
export function startOfLocalDay(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-')
  return new Date(Number(year), Number(month) - 1, Number(day))
}

/**
 * Convert a Date to a `HH:mm` clock-time string using local time, as the
 * `<input type="time">` controls in the appointment forms expect.
 */
export function toClockTimeString(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * Get today's date as an ISO calendar date string (YYYY-MM-DD) in local time.
 */
export function todayIsoDate(): string {
  return toIsoDateString(new Date())
}

/**
 * Get the ISO calendar date string (YYYY-MM-DD) for a date `days` days ago,
 * relative to today, in local time.
 */
export function isoDateDaysAgo(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return toIsoDateString(date)
}

/**
 * Get the ISO calendar date string (YYYY-MM-DD) for the first day of the month
 * before this one, in local time.
 *
 * The day is set to the 1st *before* the month is moved back: on the 31st,
 * `setMonth(month - 1)` alone would land in the month after the one being named
 * — 31 March minus a month is 3 March, because February has no 31st.
 */
export function firstDayOfPreviousMonth(): string {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() - 1)
  return toIsoDateString(date)
}

/**
 * Get the ISO calendar date string (YYYY-MM-DD) for a date `days` days from
 * today, relative to today, in local time. Use a negative value for a past date.
 */
export function isoDateDaysFromNow(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return toIsoDateString(date)
}

/**
 * The calendar day before an ISO date — the inclusive end of a period that the next one
 * starting on `isoDate` follows on from.
 */
export function dayBefore(isoDate: string): string {
  const date = startOfLocalDay(isoDate)
  date.setDate(date.getDate() - 1)
  return toIsoDateString(date)
}

/**
 * Minutes since midnight for an `HH:mm` clock time, or `null` when it is blank or
 * malformed. Callers decide what an uninterpretable time means for them.
 */
function minutesSinceMidnight(time: string): number | null {
  const parts = time.split(':')
  const hours = Number(parts[0])
  const minutes = Number(parts[1])
  if (parts.length < 2 || Number.isNaN(hours) || Number.isNaN(minutes)) return null
  return hours * 60 + minutes
}

/** Render minutes since midnight as a zero-padded `HH:mm` clock time. */
function formatClockTime(minutesOfDay: number): string {
  const hours = Math.floor(minutesOfDay / 60)
  const minutes = minutesOfDay % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Add a number of minutes to an `HH:mm` clock time and return the resulting
 * `HH:mm` string. The result wraps around midnight (a day is 1440 minutes), so
 * `addMinutesToTime('23:30', 60)` yields `'00:30'`. Returns an empty string when
 * the input time is blank or malformed, so callers can leave the field untouched.
 */
export function addMinutesToTime(time: string, minutesToAdd: number): string {
  const clockMinutes = minutesSinceMidnight(time)
  if (clockMinutes === null) return ''

  return formatClockTime((((clockMinutes + minutesToAdd) % 1440) + 1440) % 1440)
}

/**
 * Round an `HH:mm` clock time to the nearest multiple of `stepMinutes`, resolving
 * a half-step exactly between two multiples upwards (`09:07` → `09:05`, `09:08` →
 * `09:10`).
 *
 * The result never leaves the day: a time that would round up past midnight is
 * clamped to the last multiple before it (`23:58` at a 5-minute step → `23:55`),
 * since an appointment's start and end share one calendar date and `00:00` would
 * turn a valid slot into an end-before-start one.
 *
 * A blank, malformed or non-positive-step input is returned unchanged, so callers
 * can round unconditionally and leave anything they cannot interpret alone.
 */
export function roundTimeToStep(time: string, stepMinutes: number): string {
  const clockMinutes = minutesSinceMidnight(time)
  if (clockMinutes === null) return time
  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) return time

  const lastStepOfDay = Math.floor(1440 / stepMinutes) - 1
  const steps = Math.min(Math.round(clockMinutes / stepMinutes), lastStepOfDay)
  return formatClockTime(steps * stepMinutes)
}

/**
 * Calculate a person's age in whole years from a date of birth, relative to
 * today (local time). Returns null when the date of birth is missing, invalid,
 * or in the future.
 */
export function calculateAge(dateOfBirth: string | number | Date | null | undefined): number | null {
  if (dateOfBirth == null || dateOfBirth === '') return null
  const birthDate = new Date(dateOfBirth)
  if (Number.isNaN(birthDate.getTime())) return null

  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age >= 0 ? age : null
}
